import React, { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Link } from "react-router-dom"
import { URL } from "../../store/actions/depannageActions"
import {
	fetchInformations,
	terminerSignalement,
} from "../../store/actions/informationActions"
import { showToast } from "../../store/actions/toastActions"
import { COLORS } from "../../utilities/Constants"
import {
	labelCategorie,
	estAutoExpire,
	lienNotification,
} from "../../config/notificationCategories"
import CustomModal from "../basics/interface/CustomModal"
import Loader from "../basics/Loader"

// Page /notifications : généralisation de l'ancienne page /signalements —
// toutes les catégories lisibles par l'utilisateur, filtrables. Hiérarchie des
// sélecteurs V2 : statut = segmented (principal), catégorie = pills
// (secondaire). Le bouton « Terminer » n'existe que pour les catégories à
// cycle « traite » (les auto_expire disparaissent d'elles-mêmes).

// Les photos sont sérialisées avec une url relative (/media/...) → on préfixe
// par l'hôte de l'API.
const MEDIA = URL.replace(/\/$/, "")

const FILTRES = [
	{ key: "actifs", label: "Actifs" },
	{ key: "termines", label: "Terminés" },
	{ key: "tous", label: "Tous" },
]

// Nombre d'éléments par page (« Charger plus » tant que le backend en renvoie
// une page pleine).
const PAGE_SIZE = 20

const styles = {
	// Contenu centré, largeur plafonnée (lisibilité des cartes texte).
	page: {
		padding: 24,
		maxWidth: 760,
		margin: "0 auto",
		fontFamily: "'DM Sans', 'Helvetica Neue', sans-serif",
		color: COLORS.text,
	},
	title: { fontSize: 22, fontWeight: 700, marginBottom: 16 },
	filters: { display: "flex", gap: 8, marginBottom: 12 },
	filterBtn: (active) => ({
		padding: "7px 16px",
		borderRadius: 8,
		border: `1px solid ${active ? COLORS.accent : COLORS.border}`,
		backgroundColor: active ? COLORS.accentLight : COLORS.surface,
		color: active ? COLORS.accent : COLORS.textMuted,
		fontSize: 14,
		fontWeight: 600,
		cursor: "pointer",
		fontFamily: "inherit",
	}),
	// Pills catégorie (sélecteur secondaire, cf. hiérarchie V2).
	pills: { display: "flex", gap: 6, marginBottom: 20, flexWrap: "wrap" },
	pill: (active) => ({
		padding: "4px 12px",
		borderRadius: 99,
		border: `1px solid ${active ? COLORS.accent : COLORS.border}`,
		backgroundColor: active ? COLORS.accentLight : COLORS.surface,
		color: active ? COLORS.accent : COLORS.textMuted,
		fontSize: 12,
		fontWeight: 600,
		cursor: "pointer",
		fontFamily: "inherit",
	}),
	list: { display: "flex", flexDirection: "column", gap: 12 },
	card: {
		border: `1px solid ${COLORS.border}`,
		borderRadius: 12,
		backgroundColor: COLORS.surface,
		padding: 16,
	},
	cardHeader: {
		display: "flex",
		justifyContent: "space-between",
		fontSize: 12,
		color: COLORS.textLight,
		marginBottom: 6,
	},
	catBadge: {
		fontSize: 11,
		fontWeight: 700,
		color: COLORS.textMuted,
		textTransform: "uppercase",
		letterSpacing: 0.4,
	},
	cat: { fontSize: 16, fontWeight: 600, marginBottom: 4 },
	texte: { fontSize: 14, color: COLORS.textMuted, whiteSpace: "pre-wrap" },
	lien: {
		display: "inline-block",
		marginTop: 6,
		fontSize: 13,
		fontWeight: 600,
		color: COLORS.accent,
		textDecoration: "none",
	},
	photos: { display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 },
	photo: {
		width: 90,
		height: 90,
		objectFit: "cover",
		borderRadius: 8,
		border: `1px solid ${COLORS.border}`,
	},
	footerRow: {
		display: "flex",
		justifyContent: "flex-end",
		marginTop: 12,
	},
	terminerBtn: {
		padding: "7px 14px",
		borderRadius: 8,
		border: `1px solid ${COLORS.border}`,
		backgroundColor: COLORS.surface,
		color: COLORS.text,
		fontSize: 13,
		fontWeight: 600,
		cursor: "pointer",
		fontFamily: "inherit",
	},
	badgeTermine: {
		fontSize: 12,
		fontWeight: 600,
		color: COLORS.success,
	},
	empty: {
		padding: "40px 0",
		textAlign: "center",
		color: COLORS.textLight,
		fontSize: 14,
	},
	loadMoreRow: {
		display: "flex",
		justifyContent: "center",
		marginTop: 16,
	},
	loadMoreBtn: {
		padding: "8px 20px",
		borderRadius: 8,
		border: `1px solid ${COLORS.border}`,
		backgroundColor: COLORS.surface,
		color: COLORS.text,
		fontSize: 13,
		fontWeight: 600,
		cursor: "pointer",
		fontFamily: "inherit",
	},
}

const NotificationsPage = () => {
	const dispatch = useDispatch()
	const { informations, fetchInformations: fetchState } = useSelector(
		(state) => state.informationsInfo,
	)
	const [statut, setStatut] = useState("actifs")
	// null = toutes les catégories lisibles.
	const [categorie, setCategorie] = useState(null)
	// Notification en attente de confirmation « Terminer » (null = pas de modal).
	const [aTerminer, setATerminer] = useState(null)
	const [saving, setSaving] = useState(false)
	// Pagination : hasMore = la dernière page reçue était pleine.
	const [hasMore, setHasMore] = useState(false)
	const [loadingMore, setLoadingMore] = useState(false)

	// Catégories consultables = celles auxquelles on est éligible (cases cochées
	// dans Configuration → Notifications), résolues serveur.
	const lisibles = useSelector((state) => state.accessInfo.notifCategories)
	// Les pills n'apparaissent que s'il y a un choix à faire.
	const montrerPills = lisibles.length > 1

	const fetchOpts = useMemo(
		() => (categorie ? { categorie } : {}),
		[categorie],
	)

	useEffect(() => {
		dispatch(
			fetchInformations(statut, {
				limit: PAGE_SIZE,
				offset: 0,
				...fetchOpts,
			}),
		).then((json) => setHasMore((json || []).length === PAGE_SIZE))
	}, [dispatch, statut, fetchOpts])

	const chargerPlus = () => {
		setLoadingMore(true)
		dispatch(
			fetchInformations(statut, {
				limit: PAGE_SIZE,
				offset: informations.length,
				append: true,
				...fetchOpts,
			}),
		)
			.then((json) => setHasMore((json || []).length === PAGE_SIZE))
			.finally(() => setLoadingMore(false))
	}

	const confirmerTerminer = () => {
		if (!aTerminer) return
		setSaving(true)
		// Recharge autant d'éléments qu'affichés (le terminé sort des
		// « Actifs » sans réduire la liste à une page).
		const reloadLimit = Math.max(PAGE_SIZE, informations.length)
		dispatch(
			terminerSignalement(aTerminer.id, statut, {
				limit: reloadLimit,
				offset: 0,
				...fetchOpts,
			}),
		)
			.then((json) => {
				setHasMore((json || []).length === reloadLimit)
				dispatch(showToast("success", "Notification terminée"))
				setATerminer(null)
			})
			.catch(() => {
				dispatch(showToast("danger", "Échec de l'opération."))
			})
			.finally(() => setSaving(false))
	}

	return (
		<div style={styles.page}>
			<h1 style={styles.title}>Notifications</h1>

			<div style={styles.filters}>
				{FILTRES.map((f) => (
					<button
						key={f.key}
						type="button"
						style={styles.filterBtn(statut === f.key)}
						onClick={() => setStatut(f.key)}
					>
						{f.label}
					</button>
				))}
			</div>

			{montrerPills && (
				<div style={styles.pills}>
					<button
						type="button"
						style={styles.pill(categorie === null)}
						onClick={() => setCategorie(null)}
					>
						Toutes
					</button>
					{lisibles.map((cle) => (
						<button
							key={`pill_${cle}`}
							type="button"
							style={styles.pill(categorie === cle)}
							onClick={() => setCategorie(cle)}
						>
							{labelCategorie(cle)}
						</button>
					))}
				</div>
			)}

			{fetchState.loading && !loadingMore ? (
				<Loader variant="ring" message="Chargement…" />
			) : informations.length === 0 ? (
				<div style={styles.empty}>Aucune notification</div>
			) : (
				<div style={styles.list}>
					{informations.map((info) => {
						const cle = info.type_notification || "signalement"
						return (
							<div key={`notif_${info.id}`} style={styles.card}>
								<div style={styles.cardHeader}>
									<span>
										{/* Catégorie visible quand on liste « Toutes ». */}
										{!categorie && (
											<span style={styles.catBadge}>
												{labelCategorie(cle)}
												{" · "}
											</span>
										)}
										{info.initiale}
									</span>
									<span>{info.created_at}</span>
								</div>
								{/* Sous-type libre du signalement (saisi mobile). */}
								{info.categorie ? (
									<div style={styles.cat}>
										{info.categorie}
									</div>
								) : null}
								<div style={styles.texte}>{info.texte}</div>
								{lienNotification(info) && (
									<Link
										to={lienNotification(info)}
										style={styles.lien}
									>
										Ouvrir
									</Link>
								)}

								{info.photos && info.photos.length > 0 && (
									<div style={styles.photos}>
										{info.photos.map((p) => (
											<a
												key={`ph_${p.id}`}
												href={`${MEDIA}${p.url}`}
												target="_blank"
												rel="noopener noreferrer"
											>
												<img
													src={`${MEDIA}${p.url}`}
													alt="photo signalement"
													style={styles.photo}
												/>
											</a>
										))}
									</div>
								)}

								{/* auto_expire : pas d'action, la notification vit et
								    meurt toute seule. */}
								{!estAutoExpire(cle) && (
									<div style={styles.footerRow}>
										{info.termine ? (
											<span style={styles.badgeTermine}>
												✓ Terminé
											</span>
										) : (
											<button
												type="button"
												style={styles.terminerBtn}
												onClick={() =>
													setATerminer(info)
												}
											>
												Terminer
											</button>
										)}
									</div>
								)}
							</div>
						)
					})}
					{hasMore && (
						<div style={styles.loadMoreRow}>
							<button
								type="button"
								style={styles.loadMoreBtn}
								onClick={chargerPlus}
								disabled={loadingMore}
							>
								{loadingMore ? "Chargement…" : "Charger plus"}
							</button>
						</div>
					)}
				</div>
			)}

			{aTerminer && (
				<CustomModal
					title="Terminer la notification ?"
					subtitle={
						aTerminer.categorie ||
						labelCategorie(aTerminer.type_notification)
					}
					onClose={() => (saving ? null : setATerminer(null))}
					buttons={[
						{
							title: "Annuler",
							styleName: "secondary",
							onClick: () => setATerminer(null),
							disabled: saving,
						},
						{
							title: saving ? "..." : "Terminer",
							styleName: "primary",
							onClick: confirmerTerminer,
							disabled: saving,
						},
					]}
				>
					<div style={{ fontSize: 14, color: COLORS.textMuted }}>
						{aTerminer.texte}
					</div>
				</CustomModal>
			)}
		</div>
	)
}

export default NotificationsPage
